import React from "react";
import { useTranslation } from "react-i18next";

interface Inspection {
  id: string;
  findings: string;
  pest_type: string | null;
  bait_status: string | null;
  actions_taken: string | null;
  notes: string | null;
  created_at: string;
  scheduled_at: string;
  technician_name: string | null;
}

interface TrendData {
  station: {
    id: string;
    code: string;
    type: string;
    location_name: string;
  };
  inspections: Inspection[];
  summary: {
    totalInspections: number;
    inspectionsWithActivity: number;
    activityRate: number;
    consecutiveActivityStreak: number;
  };
}

const LEVEL_MAP: Record<string, number> = {
  none: 0,
  low_activity: 1,
  moderate_activity: 2,
  high_activity: 3,
};

const LEVEL_COLORS: Record<string, string> = {
  none: "#10b981",         // emerald-500
  low_activity: "#fbbf24", // amber-400
  moderate_activity: "#f59e0b", // amber-500
  high_activity: "#f43f5e", // rose-500
};

const BAIT_ICONS: Record<string, string> = {
  intact: "#10b981",
  partially_consumed: "#fbbf24",
  fully_consumed: "#f43f5e",
  replaced: "#0ea5e9",
};

export function StationTrends({ data }: { data: TrendData }) {
  const { t } = useTranslation();
  const { inspections, summary, station } = data;

  if (inspections.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-navy-100 p-8 text-center">
        <p className="text-navy-400 text-sm">{t("trends.noData")}</p>
      </div>
    );
  }

  const isBait = station.type === "bait_station";

  // Chart dimensions
  const W = 600, H = 180;
  const padL = 60, padR = 20, padT = 20, padB = isBait ? 50 : 30;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const levels = ["high", "moderate", "low", "none"];
  const levelY = (findings: string) => {
    const v = LEVEL_MAP[findings] ?? 0;
    return padT + chartH - (v / 3) * chartH;
  };

  const points = inspections.map((insp, i) => {
    const x = inspections.length === 1
      ? padL + chartW / 2
      : padL + (i / (inspections.length - 1)) * chartW;
    const y = levelY(insp.findings);
    return { x, y, insp };
  });

  // Build line path
  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  return (
    <div className="bg-white rounded-xl border border-navy-100 p-5">
      <h3 className="font-semibold mb-4" style={{ fontFamily: "var(--font-display)" }}>
        {t("trends.title")}
      </h3>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="bg-navy-50 rounded-lg p-3 text-center">
          <p className="text-xl font-bold" style={{ fontFamily: "var(--font-display)" }}>{summary.totalInspections}</p>
          <p className="text-xs text-navy-400">{t("trends.totalInspections")}</p>
        </div>
        <div className="bg-navy-50 rounded-lg p-3 text-center">
          <p className={`text-xl font-bold ${summary.activityRate > 50 ? "text-rose-500" : summary.activityRate > 0 ? "text-amber-500" : "text-emerald-500"}`} style={{ fontFamily: "var(--font-display)" }}>
            {summary.activityRate}%
          </p>
          <p className="text-xs text-navy-400">{t("trends.activityRate")}</p>
        </div>
        <div className="bg-navy-50 rounded-lg p-3 text-center">
          <p className={`text-xl font-bold ${summary.consecutiveActivityStreak > 2 ? "text-rose-500" : summary.consecutiveActivityStreak > 0 ? "text-amber-500" : "text-emerald-500"}`} style={{ fontFamily: "var(--font-display)" }}>
            {summary.consecutiveActivityStreak}
          </p>
          <p className="text-xs text-navy-400">{t("trends.consecutiveStreak")}</p>
        </div>
        <div className="bg-navy-50 rounded-lg p-3 text-center">
          <p className="text-sm font-medium" style={{ fontFamily: "var(--font-display)" }}>
            {new Date(inspections[inspections.length - 1].scheduled_at).toLocaleDateString()}
          </p>
          <p className="text-xs text-navy-400">{t("trends.lastInspection")}</p>
        </div>
      </div>

      {/* SVG Chart */}
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full min-w-[400px]" style={{ maxHeight: "220px" }}>
          {/* Y-axis level labels */}
          {levels.map((level, i) => {
            const y = padT + (i / 3) * chartH;
            return (
              <g key={level}>
                <line x1={padL} x2={W - padR} y1={y} y2={y} stroke="#e2e8f0" strokeWidth={0.5} strokeDasharray="3,3" />
                <text x={padL - 8} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">
                  {t(`trends.activityLevels.${level}`)}
                </text>
              </g>
            );
          })}

          {/* Baseline */}
          <line x1={padL} x2={W - padR} y1={padT + chartH} y2={padT + chartH} stroke="#cbd5e1" strokeWidth={0.5} />

          {/* Area fill */}
          {points.length > 1 && (
            <path
              d={`${linePath} L ${points[points.length - 1].x} ${padT + chartH} L ${points[0].x} ${padT + chartH} Z`}
              fill="url(#trendGradient)"
              opacity={0.15}
            />
          )}

          {/* Line */}
          {points.length > 1 && (
            <path d={linePath} fill="none" stroke="#f59e0b" strokeWidth={2} strokeLinejoin="round" />
          )}

          {/* Data points */}
          {points.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y} r={5} fill={LEVEL_COLORS[p.insp.findings] ?? "#94a3b8"} stroke="white" strokeWidth={2} />
              {/* X-axis date labels (show every Nth to avoid overlap) */}
              {(inspections.length <= 10 || i % Math.ceil(inspections.length / 8) === 0 || i === inspections.length - 1) && (
                <text x={p.x} y={padT + chartH + 16} textAnchor="middle" fontSize={8} fill="#94a3b8">
                  {new Date(p.insp.scheduled_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </text>
              )}
              {/* Bait status indicators */}
              {isBait && p.insp.bait_status && (
                <rect
                  x={p.x - 4}
                  y={padT + chartH + 24}
                  width={8}
                  height={8}
                  rx={2}
                  fill={BAIT_ICONS[p.insp.bait_status] ?? "#94a3b8"}
                />
              )}
            </g>
          ))}

          {/* Gradient definition */}
          <defs>
            <linearGradient id="trendGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Bait status legend */}
      {isBait && (
        <div className="flex gap-4 mt-3 justify-center">
          {Object.entries(BAIT_ICONS).map(([key, color]) => (
            <div key={key} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: color }} />
              <span className="text-xs text-navy-400">{t(`inspections.baitStatus.${key}`)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
